import {
  Alert,
  Avatar,
  Button,
  FormField,
  Icon,
  Input,
  List,
  ListRow,
  Section,
  Select,
  Skeleton,
  Stack,
  useToast,
} from '@ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormDialog } from '../../../app/FormDialog';
import { PageActions } from '../../../app/PageActions';
import { errorMessage, formErrors } from '../../../lib/errors';
import { formatDate } from '../../../lib/format';
import {
  useInvitations,
  useInvite,
  useMembers,
  useRevokeInvitation,
  type Member,
  type Role,
} from '../api';
import { useSession } from '../session';
import { useGrantableRoles, useMemberActions } from './MemberActions';
import styles from './MembersPage.module.css';

/** Members of the business and open invitations (USR-005). */
export function MembersPage() {
  const { t } = useTranslation();
  const { can } = useSession();
  const canManage = can('members.manage');
  const members = useMembers();
  const grantableRoles = useGrantableRoles();
  const [inviting, setInviting] = useState(false);

  return (
    <Stack gap={6}>
      {canManage && (
        <PageActions>
          <Button iconStart={<Icon name="plus" size="sm" />} onClick={() => setInviting(true)}>
            {t('members.invite')}
          </Button>
        </PageActions>
      )}
      {members.error && <Alert tone="danger">{errorMessage(members.error)}</Alert>}
      {members.isPending && (
        <div role="status" aria-label={t('common.loading')}>
          <Skeleton lines={3} />
        </div>
      )}
      {members.data && (
        <Section title={t('members.title')}>
          <List>
            {members.data.map((m) => (
              <MemberRow
                key={m.user_id}
                member={m}
                soleOwner={m.role.owner && members.data.filter((x) => x.role.owner).length === 1}
              />
            ))}
          </List>
        </Section>
      )}
      {canManage && <Invitations />}
      <InviteDialog open={inviting} roles={grantableRoles} onClose={() => setInviting(false)} />
    </Stack>
  );
}

function MemberRow({ member, soleOwner }: { member: Member; soleOwner: boolean }) {
  const { t } = useTranslation();
  const { me } = useSession();
  const actions = useMemberActions(member, { soleOwner });
  const isSelf = member.user_id === me.id;
  const joined = t('members.joined', { date: formatDate(member.joined_at) });

  // The role as grey text on the right, then "…" if there is anything to do
  // (#36, #197). On phones the role moves below the e-mail, together with the
  // date it joined (#60).
  return (
    <ListRow
      href={`/einstellungen/mitglieder/${member.user_id}`}
      leading={<Avatar name={member.name} decorative />}
      title={isSelf ? `${member.name} (${t('members.you')})` : member.name}
      description={
        <>
          <span className={styles.email}>{member.email}</span>
          <span className={styles.joinedInline}>{` · ${joined}`}</span>
          <span className={styles.roleBelow}>{`${member.role.name} · ${joined}`}</span>
        </>
      }
      trailing={
        <>
          <span className={styles.role}>{member.role.name}</span>
          {actions.dialogs}
        </>
      }
      actionsLabel={`${t('common.moreActions')}: ${member.name}`}
      actions={actions.items}
    />
  );
}

/** Open invitations; the section is left out when there are none. */
function Invitations() {
  const { t } = useTranslation();
  const toast = useToast();
  const invitations = useInvitations(true);
  const revoke = useRevokeInvitation();

  if (!invitations.data || invitations.data.length === 0) return null;
  return (
    <Section title={t('members.invitations')}>
      <List>
        {invitations.data.map((i) => (
          <ListRow
            key={i.id}
            title={i.email}
            description={`${i.role.name} · ${t('members.expires', { date: formatDate(i.expires_at) })}`}
            trailing={
              <Button
                variant="plain"
                size="sm"
                aria-label={`${t('members.revoke')}: ${i.email}`}
                loading={revoke.isPending && revoke.variables === i.id}
                onClick={() =>
                  revoke.mutate(i.id, {
                    onSuccess: () => toast.show(t('members.revoked')),
                    onError: (error) => toast.show(errorMessage(error), 'danger'),
                  })
                }
              >
                {t('members.revoke')}
              </Button>
            }
          />
        ))}
      </List>
    </Section>
  );
}

/** The suggested role for invitations: the one granting the least. */
function defaultRole(roles: Role[]): Role | undefined {
  return [...roles]
    .filter((r) => !r.owner)
    .sort((a, b) => a.permissions.length - b.permissions.length)[0];
}

function InviteDialog({
  open,
  roles,
  onClose,
}: {
  open: boolean;
  roles: Role[];
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const invite = useInvite();
  const [email, setEmail] = useState('');
  const [chosen, setChosen] = useState<string | null>(null);
  const roleId = chosen ?? defaultRole(roles)?.id ?? '';

  const close = () => {
    invite.reset();
    onClose();
  };
  const onSubmit = () =>
    invite.mutate(
      { email, role_id: roleId },
      {
        onSuccess: (i) => {
          toast.show(t('members.invited', { email: i.email }));
          setEmail('');
          close();
        },
      },
    );
  const errors = formErrors(invite.error, { email: ['email.', 'member.'], role: ['role.'] });

  return (
    <FormDialog
      open={open}
      onClose={close}
      title={t('members.invite')}
      submitLabel={t('members.inviteSubmit')}
      onSubmit={onSubmit}
      pending={invite.isPending}
      error={errors.general}
    >
      <FormField label={t('auth.email')} error={errors.field.email} required>
        {(p) => (
          <Input
            {...p}
            type="email"
            autoComplete="off"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        )}
      </FormField>
      <FormField label={t('members.roleLabel')} error={errors.field.role} required>
        {(p) => (
          <Select
            {...p}
            value={roleId}
            onChange={(e) => setChosen(e.target.value)}
            options={roles.map((r) => ({ value: r.id, label: r.name }))}
          />
        )}
      </FormField>
    </FormDialog>
  );
}
